import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map } from 'rxjs';
import { FornitoriAdmin } from 'src/app/models/supplier-portal/supplier/fornitori-admin.models';
import { IWsResult } from 'src/app/models/ws_result.model';
import { environment } from 'src/environments/environment';
import { ErrorHandlerService } from './shared/error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class FornitoriAdminMainService {
  private readonly serviceBase = environment.urlServer;

  private readonly endpoints = {
    getSupplierReport: `${this.serviceBase}/pdf_trace_product/`,
    getSupplierOpenOrders: `${this.serviceBase}/get_supplier_open_orders/`,
    getSupplierAccounts: `${this.serviceBase}/get_supplier_accounts/`,
    findSupplier: (code: number) =>
      `${this.serviceBase}/find_supplier/${code}/`,
    saveSupplier: `${this.serviceBase}/save_supplier/`,
    statusSupplier: `${this.serviceBase}/status_supplier/`,
    getTraceProducts: `${this.serviceBase}/get_trace_products/`,
    getTraceSuppliers: (artCode: string) =>
      `${this.serviceBase}/get_trace_suppliers/${artCode}/`,
    getTraceLots: (artCode: string, forCode: string) =>
      `${this.serviceBase}/get_trace_lots/${artCode}/${forCode}/`,
    getTraceSerials: (artCode: string, forCode: string, lot: string) =>
      `${this.serviceBase}/get_trace_serials/${artCode}/${forCode}/${lot}/`,
    searchTraceProducts: `${this.serviceBase}/search_trace_products/`,
    loadTraceProduct: `${this.serviceBase}/load_trace_product/`,
    getSuppliersList: `${this.serviceBase}/get_suppliers_list/`,
    getSupplierOpenOrdersDetails: (idOrdine: number, idGamma: number, idCertificato: number) =>
      `${this.serviceBase}/get_supplier_open_orders_details/${idOrdine}/${idGamma}/${idCertificato}/`,
    getSupplierCloseOrders: `${this.serviceBase}/get_supplier_close_orders/`,
    getSupplierCloseOrdersDetails: (idOrdine: number, idGamma: number, idCertificato: number) =>
      `${this.serviceBase}/get_supplier_close_orders_details/${idOrdine}/${idGamma}/${idCertificato}/`,
  } as const;

  constructor(
    private readonly http: HttpClient,
    private readonly errorHandlerServ: ErrorHandlerService,
  ) {}

  // ── Supplier Accounts ─────────────────────────────────────────────────────

  getSupplierAccounts(): Observable<FornitoriAdmin.ISupplierListItem[]> {
    return this.getSuppliersList().pipe(
      map(response => response.items),
    );
  }

  // ── Supplier Report ─────────────────────────────────────────────────────

  getSupplierReport(payload: {
    art_qr: string;
    order_number: string;
  }): Observable<Blob> {
    return this.http.post(this.endpoints.getSupplierReport, payload, {
      responseType: 'blob',
    });
  }


  // ── Supplier Orders ───────────────────────────────────────────────────────

  getSupplierOpenOrders(): Observable<FornitoriAdmin.OpenOrder[]> {
    return this.http
      .get<IWsResult>(this.endpoints.getSupplierOpenOrders)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getSupplierOpenOrders'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (result?.success && Array.isArray(result.data))
            return result.data.map(
              (item: FornitoriAdmin.IOpenOrder) =>
                new FornitoriAdmin.OpenOrder(item),
            );
          return [];
        }),
      );
  }

  // ── Account ───────────────────────────────────────────────────────────────

  /**
   * 🚨 FIX v2.12.4 (B2): pre-v2.12.4 si faceva
   * `new FindSupplierResult(result.data)` direttamente. Quando il BE
   * ritorna `data` come array singolo `[{codice_fornitore, ...}]` (shape
   * vista in altri endpoint `get_*_list` simili lato BE), `Object.assign`
   * copia gli indici numerici dell'array invece dei campi → istanza con
   * `codice_fornitore = 0` (default) → il componente check
   * `if (result?.codice_fornitore)` cade in else → popup "Non trovato"
   * anche se il fornitore ESISTE BE-side. Fix: normalizzare data
   * (array → primo elemento) prima del wrap nel modello.
   */
  findSupplier(code: number): Observable<FornitoriAdmin.FindSupplierResult> {
    return this.http
      .get<IWsResult>(this.endpoints.findSupplier(code))
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('findSupplier'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (!result?.success) return new FornitoriAdmin.FindSupplierResult();
          const payload = Array.isArray(result.data)
            ? result.data[0]
            : result.data;
          if (!payload) return new FornitoriAdmin.FindSupplierResult();
          return new FornitoriAdmin.FindSupplierResult(payload);
        }),
      );
  }

  saveSupplier(
    input: FornitoriAdmin.ISaveSupplierInput,
  ): Observable<FornitoriAdmin.SupplierOperationResult> {
    return this.http
      .post<IWsResult>(this.endpoints.saveSupplier, input)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('saveSupplier'),
        ),
      )
      .pipe(
        map(
          (result: IWsResult) =>
            new FornitoriAdmin.SupplierOperationResult(result),
        ),
      );
  }

  statusSupplier(
    input: FornitoriAdmin.IStatusSupplierInput,
  ): Observable<FornitoriAdmin.SupplierOperationResult> {
    return this.http
      .post<IWsResult>(this.endpoints.statusSupplier, input)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('statusSupplier'),
        ),
      )
      .pipe(
        map(
          (result: IWsResult) =>
            new FornitoriAdmin.SupplierOperationResult(result),
        ),
      );
  }

  // ── Traceability ──────────────────────────────────────────────────────────

  getTraceProducts(): Observable<FornitoriAdmin.TraceProduct[]> {
    return this.http
      .get<IWsResult>(this.endpoints.getTraceProducts)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getTraceProducts'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (!result?.success) return [];
          const arr: any[] = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result.data?.products)
              ? result.data.products
              : [];
          return arr.map((item: any) => new FornitoriAdmin.TraceProduct(item));
        }),
      );
  }

  getTraceSuppliers(
    artCode: string,
  ): Observable<FornitoriAdmin.TraceSupplier[]> {
    return this.http
      .get<IWsResult>(this.endpoints.getTraceSuppliers(artCode))
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getTraceSuppliers'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (!result?.success) return [];
          const arr: any[] = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result.data?.suppliers)
              ? result.data.suppliers
              : [];
          return arr.map((item: any) => new FornitoriAdmin.TraceSupplier(item));
        }),
      );
  }

  getTraceLots(
    artCode: string,
    forCode: string,
  ): Observable<FornitoriAdmin.TraceBatch[]> {
    return this.http
      .get<IWsResult>(this.endpoints.getTraceLots(artCode, forCode))
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getTraceLots'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (!result?.success) return [];
          const arr: any[] = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result.data?.lots)
              ? result.data.lots
              : [];
          return arr.map((item: any) => new FornitoriAdmin.TraceBatch(item));
        }),
      );
  }

  getTraceSerials(
    artCode: string,
    forCode: string,
    lot: string,
  ): Observable<FornitoriAdmin.TraceSerial[]> {
    return this.http
      .get<IWsResult>(this.endpoints.getTraceSerials(artCode, forCode, lot))
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getTraceSerials'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (!result?.success) return [];
          const arr: any[] = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result.data?.serials)
              ? result.data.serials
              : [];
          return arr.map((item: any) => new FornitoriAdmin.TraceSerial(item));
        }),
      );
  }

  searchTraceProducts(
    input: FornitoriAdmin.ITraceSearchInput,
  ): Observable<FornitoriAdmin.TraceSearchResult[]> {
    return this.http
      .post<IWsResult>(this.endpoints.searchTraceProducts, input)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('searchTraceProducts'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (result?.success && Array.isArray(result.data))
            return result.data.map(
              (item: any) => new FornitoriAdmin.TraceSearchResult(item),
            );
          return [];
        }),
      );
  }

  getSuppliersList(
    input: FornitoriAdmin.IGetSuppliersListInput = {},
  ): Observable<FornitoriAdmin.GetSuppliersListResponse> {
    return this.http
      .request<IWsResult>('GET', this.endpoints.getSuppliersList, {
        body: input,
      })
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getSuppliersList'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (result?.success)
            return new FornitoriAdmin.GetSuppliersListResponse(result.data);
          return new FornitoriAdmin.GetSuppliersListResponse();
        }),
      );
  }

  getSupplierCloseOrdersDetails(
    idOrdine: number,
    idGamma: number,
    idCertificato: number,
  ): Observable<FornitoriAdmin.OrderDetail> {
    return this.http
      .get<IWsResult>(this.endpoints.getSupplierCloseOrdersDetails(idOrdine, idGamma, idCertificato))
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>(
            'getSupplierCloseOrdersDetails',
          ),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (result?.success)
            return new FornitoriAdmin.OrderDetail(result.data);
          return new FornitoriAdmin.OrderDetail();
        }),
      );
  }

  getSupplierCloseOrders(): Observable<FornitoriAdmin.OpenOrder[]> {
    return this.http
      .get<IWsResult>(this.endpoints.getSupplierCloseOrders)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('getSupplierCloseOrders'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (result?.success && Array.isArray(result.data))
            return result.data.map(
              (item: FornitoriAdmin.IOpenOrder) =>
                new FornitoriAdmin.OpenOrder(item),
            );
          return [];
        }),
      );
  }

  getSupplierOpenOrdersDetails(
    idOrdine: number,
    idGamma: number,
    idCertificato: number,
  ): Observable<FornitoriAdmin.OrderDetail> {
    return this.http
      .get<IWsResult>(this.endpoints.getSupplierOpenOrdersDetails(idOrdine, idGamma, idCertificato))
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>(
            'getSupplierOpenOrdersDetails',
          ),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (result?.success)
            return new FornitoriAdmin.OrderDetail(result.data);
          return new FornitoriAdmin.OrderDetail();
        }),
      );
  }

  loadTraceProduct(
    input: FornitoriAdmin.ILoadTraceProductInput,
  ): Observable<FornitoriAdmin.TraceProductHistory | null> {
    return this.http
      .post<IWsResult>(this.endpoints.loadTraceProduct, input)
      .pipe(
        catchError(
          this.errorHandlerServ.handleError<IWsResult>('loadTraceProduct'),
        ),
      )
      .pipe(
        map((result: IWsResult) => {
          if (!result?.success) return null;
          const raw = Array.isArray(result.data) ? result.data[0] : result.data;
          return raw ? new FornitoriAdmin.TraceProductHistory(raw) : null;
        }),
      );
  }
}
